import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { client } from "@/lib/sanity";
import { PortableText } from "@portabletext/react";

import TitleBar from "@/components/elements/TitleBar/TitleBar";
import ImageWithContentLeft from "@/components/elements/ImageWithContent/ImageWithContentLeft";
import TitleWithContent from "@/components/elements/TitleWithContent/TitleWithContent";
import CustomLists from "@/components/elements/CustomLists/CustomLists";

const fadeInUp = {
  initial: { opacity: 0, y: 50 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: "easeOut" },
  viewport: { once: false, amount: 0.2 },
};

// PortableText components with paragraph styling and list overrides
const portableComponents = {
  block: {
    normal: ({ children }: any) => (
      <p
        className="table-text-size text-justify tracking-tight break-normal word-spacing-wide leading-relaxed"
        style={{ all: 'unset', display: 'block' }} // reset inherited styles, keep block display
      >
        {children}
      </p>
    ),
  },
};

const splitIntoColumns = (arr: string[], columns = 2): string[][] => {
  const result: string[][] = Array.from({ length: columns }, () => []);
  arr.forEach((item, index) => {
    result[index % columns].push(item);
  });
  return result;
}; 

const SanityServiceDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [service, setService] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;

    const fetchService = async () => {
      const query = `*[_type == "service" && slug.current == $slug][0]{
        title,
        "imageUrl": thumbnail.asset->url,
        description,
        advantages,
        applications,
        types,
        installation,
        whyChooseUs,
        whyChooseUsPoints,
        customBullets,
        additionalInfo
      }`;
      try {
        const result = await client.fetch(query, { slug });
        setService(result);
      } catch (err) {
        console.error("Sanity fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [slug]);

  if (loading) return <p className="p-10 text-center">Loading...</p>;
  if (!service) return <p className="p-10 text-center">Service not found</p>;

  return (
    <div className="pb-0 px-4 2xl:px-40 xl:px-40 md:px-[3rem] my-[5rem]">
      <TitleBar title={service.title} />

      {service.imageUrl && (
        <motion.div {...fadeInUp}>
          <ImageWithContentLeft
            imageUrl={service.imageUrl}
            title="Description"
            content={service.description}
          />
        </motion.div>
      )}

      {service.advantages?.length > 0 && (
        <motion.div {...fadeInUp}>
          <TitleWithContent title={`Advantages of ${service.title}`}>
            <CustomLists layout="split" items={splitIntoColumns(service.advantages, 2)}/>
          </TitleWithContent>
        </motion.div>
      )}

      {service.applications?.length > 0 && (
        <motion.div {...fadeInUp}>
          <TitleWithContent title={`Applications of ${service.title}`}>
            <CustomLists layout="split" items={splitIntoColumns(service.applications, 2)} />
          </TitleWithContent>
        </motion.div>
      )}

      {service.types?.filter((item: string) => item?.trim()).length > 0 && (
        <motion.div {...fadeInUp}>
          <TitleWithContent title={`Types of ${service.title}`}>
            <CustomLists
              layout="stacked"
              items={[service.types.filter((item: string) => item?.trim())]}
            />
          </TitleWithContent>
        </motion.div>
      )}

      {service.installation?.filter((item: string) => item?.trim()).length > 0 && (
        <motion.div {...fadeInUp}>
          <TitleWithContent title={`Installation Process of ${service.title}`}>
            <CustomLists
              listStyle="decimal"
              layout="stacked"
              items={[service.installation.filter((item: string) => item?.trim())]}
            />
          </TitleWithContent>
        </motion.div>
      )}

      {service.whyChooseUs && (
        <motion.div {...fadeInUp}>
          <TitleWithContent title={`Why Choose Us for ${service.title}?`}>
            <PortableText value={service.whyChooseUs} components={portableComponents} />

            {service.whyChooseUsPoints?.length > 0 && (
              <CustomLists
                listStyle="none"
                layout="stacked"
                items={[service.whyChooseUsPoints]}
                bullets={[service.customBullets ?? []]}
              />
            )}

            <p className="table-text-size text-justify tracking-tight break-normal word-spacing-wide leading-relaxed">
              <a href="/contact" className="text-custom-hov underline">
                Contact us today
              </a>{" "}
              for a free consultation or site inspection.
            </p>

            {/* Optional additional PortableText below the contact link */}
            {service.additionalInfo && (
              <PortableText value={service.additionalInfo} components={portableComponents} />
            )}
          </TitleWithContent>
        </motion.div>
      )}
    </div>
  );
};

export default SanityServiceDetailPage;
